<!--
  @name: EchartsBoxLine
  @description: echarts线下图基础样式组件
-->
<template>
  <custom-echarts-box
    ref="echartsRef"
    v-bind="$attrs"
    box-h="300px"
    :option="defaultBasicOption"
    :loading="loading"
  />
</template>
<script setup lang="ts">
import { nextTick, PropType, reactive, ref, watch } from 'vue';
import CustomEchartsBox from './index.vue';
import { type ECOption, type SeriesOption } from './setupEcharts';
import { isNull } from '@core/tools';

defineOptions({ name: 'CustomEchartsLine' });

const props = defineProps({
  dataSet: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  // 系列配置
  seriesOption: {
    type: Object as PropType<SeriesOption>,
    default: () => ({} as SeriesOption),
  },
  // 图表配置
  option: {
    type: Object as PropType<ECOption>,
    default: () => ({} as ECOption),
  },
});

const echartsRef = ref();

// 每个系列默认配置
const defaultSeriesOption = reactive<SeriesOption>({
  type: 'bar',
  name: '总收益(元)',
  barGap: '20%', // 柱子之间的间距，设置为20%大约相当于8px
  barCategoryGap: '40%', // 类目间柱形的间距
  barMaxWidth: 14,
  barMinWidth: 14,
  itemStyle: { borderRadius: [14, 14, 0, 0] },
});

// echarts默认基础图表配置
const defaultBasicOption = ref<ECOption>({
  // 标题组件
  title: {},
  // 网格组件
  grid: {
    left: 0,
    right: 0,
    top: 60,
    bottom: 0,
    containLabel: true,
  },
  // 控制图标组件
  legend: {
    // type: 'scroll',
    icon: 'circle',
    padding: [20, 0, 0, 80],
    align: 'auto',
    top: 'top',
    left: 'right',
    itemWidth: 8,
    itemHeight: 8,
    itemGap: 20,
    textStyle: {
      color: '#00000099',
      fontSize: 12,
    },
    data: ['总收益(元)', '推广服务费(元)', '溢价服务费(元)'],
  },
  tooltip: {
    trigger: 'axis',
    padding: 16,
    textStyle: {
      fontSize: 12,
      color: '#000',
    },
    axisPointer: { type: 'none' },
  },
  xAxis: {
    type: 'category',
    // boundaryGap: false, //是否设置边界
    nameGap: 35,
    axisTick: { show: false },
    axisLine: {
      // 轴线配置
      show: false,
      lineStyle: { color: '#e7e7e7' },
    },
    axisLabel: { color: '#595959' },
  },
  yAxis: {
    type: 'value',
    name: '',
    nameLocation: 'end',
    nameGap: 20,
    nameTextStyle: {
      color: '#000',
      fontSize: 14,
      lineHeight: 22,
      align: 'center',
    },
    axisLabel: {
      formatter: '￥{value}',
      color: '#595959',
    },
  },
  dataZoom: {
    type: 'inside',
    start: 0,
    endValue: 5, // 设置超过传入数据的索引值的数据进行滑动
    zoomLock: true,
  },
  dataset: { source: [] },
  series: [
    {
      name: '总收益(元)',
      type: 'bar',
      data: [], 
    },
    {
      name: '推广服务费(元)',
      type: 'bar',
      data: [], 
    },
    {
      name: '溢价服务费(元)',
      type: 'bar',
      data: [], 
    },
  ],
});

watch(
  () => props.option,
  (newV) => {
    echartsRef.value?.getEcInstance().setOption(newV);
  },
  { immediate: true },
);

watch(
  () => props.seriesOption,
  (newV) => {
    Object.assign(defaultSeriesOption, newV);
  },
  { immediate: true },
);

watch(
  () => props.dataSet,
  (newV) => {
    if (!isNull(newV) && newV.length > 0) {
      nextTick(() => {
        let names = Array.isArray(newV[0]) ? newV[0] : [];
        let seriesOptions = [];

        names.forEach((name, ind) => {
          seriesOptions.push(
            Object.assign(
              { name },
              defaultSeriesOption,
            ),
          );
        });
        // 创建一个不包含itemStyle的默认配置副本
        const baseSeriesOption = { ...defaultSeriesOption };

        delete baseSeriesOption.itemStyle;
        
        echartsRef.value?.getEcInstance().setOption(
          {
            xAxis: { data: newV[0] },
            dataset: { source: newV },
            series: [
              { 
                ...baseSeriesOption,
                data: newV[1],
                name: '总收益(元)',
                itemStyle: {
                  color: '#649DFE',
                  borderRadius: [14, 14, 0, 0], 
                }, 
              },
              { 
                ...baseSeriesOption,
                data: newV[2],
                name: '推广服务费(元)',
                itemStyle: {
                  color: '#78DFB2',
                  borderRadius: [14, 14, 0, 0], 
                }, 
              }, 
              { 
                ...baseSeriesOption,
                data: newV[3],
                name: '溢价服务费(元)',
                itemStyle: {
                  color: '#FED776',
                  borderRadius: [14, 14, 0, 0], 
                }, 
              }, 
            ],
          },
          { replaceMerge: 'series' },
        );
      });
    }
  },
  { immediate: true },
);
</script>